import React, { useState, useEffect, Suspense, useRef } from "react";
import { createPortal } from "react-dom";
import { screenConfigRegistry, walkComponentTree } from "../../state/utils/configUtils";
import { ComponentConfig, LayoutTypes, ComponentType } from "../../services/types/ComponentTypes";
import { apiService } from "../../services/api";
import { useAppDispatch } from "../../state/hooks";
import { resetScreenFormData } from "../../state/slices/formsSlice";
import loaderLight from "../../../public/images/loader_light.gif";
import loaderDark from "../../../public/images/loader_dark.gif";

// Lazy load ComponentRenderer to avoid circular dependencies
const ComponentRenderer = React.lazy(() =>
    import("../ComponentRenderer").then(module => ({ default: module.ComponentRenderer }))
);

interface QuickAddModalProps {
    popupScreenId: string;
    onClose: () => void;
    onSuccess: (newItem: any) => void;
    isOpen: boolean;
}

export const QuickAddModal: React.FC<QuickAddModalProps> = ({
    popupScreenId,
    onClose,
    onSuccess,
    isOpen,
}) => {
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState<Record<string, any>>({});

    // Wrapper to clear state on close
    const handleClose = () => {
        if (popupScreenId) {
            dispatch(resetScreenFormData(popupScreenId));
        }
        setFormData({});
        onClose();
    };

    // Wrapper to clear state on success
    const handleSuccess = (item: any) => {
        if (popupScreenId) {
            dispatch(resetScreenFormData(popupScreenId));
        }
        setFormData({});
        onSuccess(item);
        onClose();
    };
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [screenConfig, setScreenConfig] = useState<ComponentConfig[] | null>(null);

    // Load screen configuration
    useEffect(() => {
        if (isOpen && popupScreenId) {
            // Look up in registry
            const config = screenConfigRegistry[popupScreenId];
            if (config) {
                setScreenConfig(config);
            } else {
                setError(`Configuration not found for screen ID: ${popupScreenId}`);
            }
        }
    }, [isOpen, popupScreenId]);

    const handleFormDataChange = (name: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const deriveModuleName = (config: ComponentConfig[]): string | undefined => {
        let moduleName: string | undefined;

        // Find the first FORM component to get Name (priority) or TableName
        // User specifically requested /pabws/module/Customer_Add, so we need "Customer_Add" which is the Name
        if (config && config.length > 0) {
            if (Array.isArray(config)) {
                for (const comp of config) {
                    if (comp.componentType === LayoutTypes.SCREEN_GROUP && comp.childComponents) {
                        const form = comp.childComponents.find(c => c.componentType === LayoutTypes.FORM) as any;
                        if (form) {
                            return form.Name || form.TableName;
                        }
                    }
                    if (comp.componentType === LayoutTypes.FORM) {
                        return (comp as any).Name || (comp as any).TableName;
                    }
                }
            }
        }

        // Fallback: search recursively
        if (config) {
            walkComponentTree(config, (component) => {
                if (component.componentType === LayoutTypes.FORM) {
                    moduleName = (component as any).Name || (component as any).TableName;
                    return true; // Stop search
                }
                return false;
            });
        }

        return moduleName;
    };

    const handleActionTrigger = async (action: string, data?: any) => {
        console.log("[QuickAddModal] Action triggered:", action, data);

        // Handle Save action
        if (action.toLowerCase() === "save" || action.toLowerCase() === "submit") {
            await handleSave();
        } else if (action.toLowerCase() === "cancel") {
            handleClose();
        }
    };

    // Helper for generating client-side UUIDs
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Helper for formatting date as DD-MM-YYYY HH:mm:ss
    const formatDate = (date: Date) => {
        const pad = (n: number) => n.toString().padStart(2, '0');
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };

    const handleSave = async () => {
        if (!screenConfig) return;

        const moduleName = deriveModuleName(screenConfig);
        if (!moduleName) {
            setError("Could not determine module name for saving.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // 1. Get User Info
            let userId = "1";
            let branchId = "";
            let username = ""; // Needed for ID generation or just tracking

            try {
                const { authService } = await import("../../services/auth");
                const encryptedUserData = localStorage.getItem("encryptedUserData");
                if (encryptedUserData) {
                    const decryptedData = authService.decryptData(encryptedUserData);
                    if (decryptedData) {
                        userId = decryptedData.empId || decryptedData.userId || decryptedData.id || "1";
                        branchId = decryptedData.branch_id || decryptedData.branchId || decryptedData.transaction_branch_id || "";
                        username = decryptedData.empName || decryptedData.userName || "";
                    }
                }
            } catch (authError) {
                console.warn("[QuickAddModal] Failed to retrieve user info:", authError);
            }

            // 2. Generate IDs and Timestamps
            const currentTimestamp = formatDate(new Date());
            const generatedId = generateUUID(); // Primary ID

            // Generate parentid with specific format if needed, or just use UUID
            // configThunks uses a complex format: uuid_USR_empId_timestamp_1
            // We'll mimic a simple UUID for now, or try to match if strict format is required.
            // User sample: 3640361e8666461f9f3b5ca415c1f4f2_USR_000001_260121114112257_1
            // Let's assume standard UUID is acceptable, or basic concatenation. 
            // Matching the user's sample structure roughly:
            const rawTimestamp = new Date().toISOString().replace(/[-:T.]/g, "").slice(2, 14); // YYMMDDHHMMSS
            const complexId = `${generateUUID().replace(/-/g, "")}_USR_${userId.padStart(6, '0')}_${rawTimestamp}_1`;

            // User Example ID is complex, let's use the complex one to be safe
            const finalId = complexId;

            // 3. Map Fields
            const mappedData: Record<string, any> = {};

            // Helper to recursively map fields
            const mapFields = (components: ComponentConfig[]) => {
                components.forEach(comp => {
                    const compAny = comp as any;
                    const fieldName = compAny.name || compAny.BindValue;
                    const jsonAttr = compAny.Json_Attribute;

                    if (fieldName && jsonAttr) {
                        const value = formData[fieldName];

                        // Only map if value exists or is specifically empty string (if we want to overwrite)
                        // User request implies we want everything present
                        if (value !== undefined) {
                            mappedData[jsonAttr] = value;

                            // Also keep the original field name mapping as seen in user's "Actual Payload"
                            // (e.g. txt_customer_name is present along with customername)
                            mappedData[fieldName] = value;
                        }

                        // Special handling for Dropdowns to get descriptions
                        if ((comp.componentType === ComponentType.SELECT_DROPDOWN || comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) && value) {

                            // Try to find selected data from formData (ComponentRenderer convention: fieldName + '_selectedData')
                            const selectedData = formData[`${fieldName}_selectedData`];

                            if (selectedData) {
                                // Map descriptions based on lookup conventions
                                // Usually [jsonAttr]_desc or [lookupName]_desc
                                // User sample: customertype -> customertype_desc, country -> country_desc
                                const descKey = `${jsonAttr}_desc`;
                                mappedData[descKey] = selectedData.name || selectedData.label || selectedData.value || "";

                                // Also add raw select data fields if needed, user sample has s_select_customertype and s_search_country
                            }
                        }
                    }

                    if (comp.childComponents) mapFields(comp.childComponents);
                    if (compAny.Fields) mapFields(compAny.Fields);
                });
            };

            if (screenConfig) mapFields(screenConfig);

            // 4. Construct Final Payload Data
            // Merge mapped data with tracking fields
            const payloadData = {
                ...mappedData,
                // Add subform array (empty for now as Quick Add usually handles flat forms)
                subform: [],
                id: finalId,
                created_by: userId, // User requested uuid in created_by, we have userId. 
                // If specific format needed, we use what we decrypted.
                created_at: currentTimestamp,
                transaction_branch_id: branchId,

                // Parent ID logic
                parentid: finalId, // Often same as ID for root records or empty
                parent_id: finalId,
                parent_id_desc: "", // Empty for new root record
            };

            // 5. Wrap in standard structure
            const finalPayload = {
                data: payloadData,
                next_action: "",
                next_state: ""
            };

            const endpoint = `/pabws/module/${moduleName}`;

            console.log("[QuickAddModal] Saving to:", endpoint, finalPayload);

            const response = await apiService.put(endpoint, finalPayload, {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw"
            });

            if (response && (response.success || (response as any).id || (response as any).data)) {
                const newItem = (response as any).data || response;
                handleSuccess(newItem);
            } else {
                throw new Error("Save operation failed.");
            }

        } catch (err: any) {
            console.error("[QuickAddModal] Save Error:", err);
            setError(err.message || "Failed to save record.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const isDarkMode = document.documentElement.classList.contains('dark');

    return createPortal(
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-in">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {/* {screenConfig && (screenConfig[0] as any).title ? `Add ${(screenConfig[0] as any).title}` : "Quick Add"} */}
                    </h3>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar relative">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
                            {error}
                        </div>
                    )}

                    {isLoading && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center z-10 backdrop-blur-[1px]">
                            <img src={isDarkMode ? loaderDark : loaderLight} alt="Loading" className="w-12 h-12" />
                        </div>
                    )}

                    <Suspense fallback={
                        <div className="flex justify-center items-center h-40">
                            <img src={isDarkMode ? loaderDark : loaderLight} alt="Loading" className="w-10 h-10" />
                        </div>
                    }>
                        {screenConfig ? (
                            <ComponentRenderer
                                config={screenConfig}
                                formData={formData}
                                onFormDataChange={handleFormDataChange}
                                onActionTrigger={handleActionTrigger}
                                refreshKey={0}
                            />
                        ) : (
                            <div className="text-center text-gray-500">Loading configuration...</div>
                        )}
                    </Suspense>
                </div>
            </div>
        </div>,
        document.body
    );
};
